import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  ChevronsLeft,
  ChevronsRight,
  Bell,
  Calculator,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  ClipboardList,
  RefreshCw,
  Truck,
  UserCog,
  UsersRound,
  X,
} from "lucide-react";
import logo from "../assets/logo-internacional.jpg";
import AppFooter from "./AppFooter";
import NotificationBell from "./NotificationBell";
import { useAuth } from "../context/AuthContext";

const titles = {
  "/usuarios": ["Usuarios", "Administradores, vendedores y cajeros internos."],
  "/": ["Panel general", "Resumen operativo de clientes, ventas y cuentas."],
  "/clientes/pacientes": ["Clientes pacientes", "Alta, edicion y consulta de pacientes."],
  "/clientes/profesionales": ["Clientes profesionales", "Alta, edicion y consulta de profesionales."],
  "/proveedores": ["Proveedores", "Alta, edicion y productos asociados."],
  "/cotizaciones": ["Cotizaciones", "Simulacion de recetas, costos y precios de venta."],
  "/productos": ["Productos", "Catalogo unico de productos del sistema."],
  "/mercaderia": ["Ingresos de envases/materias primas", "Ingreso, trazabilidad y tracking de lotes."],
  "/materiales-produccion": ["Materiales de produccion", "Catalogo de materiales y precios unitarios."],
  "/notificaciones": ["Notificaciones", "Productos que alcanzaron el stock minimo."],
  "/operaciones": ["Operaciones", "Pedidos, stock, produccion y fraccionado."],
  "/operaciones/pedidos": ["Ordenes de pedido", "Pedidos pendientes y finalizados."],
  "/operaciones/fraccionamiento": ["Ordenes de fraccionamiento", "Pendientes y finalizadas."],
  "/operaciones/produccion": ["Ordenes de produccion", "Pendientes y finalizadas."],
};

export default function AppLayout() {
  const { usuario, logout } = useAuth();
  const location = useLocation();
  const [title, subtitle] = titles[location.pathname] || titles["/"];
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const clientesMenuActivo = location.pathname.startsWith("/clientes/");
  const operacionesMenuActivo =
    location.pathname === "/operaciones" || location.pathname.startsWith("/operaciones/");

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  return (
    <div
      className={`app-shell${sidebarOpen ? " is-sidebar-open" : ""}${
        sidebarCollapsed ? " is-sidebar-collapsed" : ""
      }`}
    >
      <button
        type="button"
        className="app-backdrop"
        aria-label="Cerrar menu"
        onClick={() => setSidebarOpen(false)}
      />
      <aside className="sidebar">
        <div className="sidebar-head">
          <div className="brand">
            <img src={logo} alt="La Internacional" />
            <div className="brand-copy">
              <strong>LaInternacional</strong>
              <span>Sistema administrativo</span>
            </div>
          </div>

          <button
            type="button"
            className="sidebar-toggle sidebar-toggle-desktop"
            aria-label={sidebarCollapsed ? "Expandir menu" : "Contraer menu"}
            aria-expanded={!sidebarCollapsed}
            title={sidebarCollapsed ? "Expandir menu" : "Contraer menu"}
            onClick={() => setSidebarCollapsed((current) => !current)}
          >
            {sidebarCollapsed ? (
              <ChevronsRight size={18} />
            ) : (
              <ChevronsLeft size={18} />
            )}
          </button>

          <button
            type="button"
            className="sidebar-toggle sidebar-toggle-mobile"
            aria-label="Cerrar menu"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={18} />
          </button>
        </div>

        <nav className="nav-menu">
          <NavLink
            className="nav-link"
            to="/"
            end
            onClick={() => setSidebarOpen(false)}
          >
            <LayoutDashboard size={18} />
            <span>Inicio</span>
          </NavLink>
          <NavLink
            className={`nav-link${clientesMenuActivo ? " active" : ""}`}
            to="/clientes/pacientes"
            onClick={() => setSidebarOpen(false)}
          >
            <UsersRound size={18} />
            <span>Clientes</span>
          </NavLink>
          {clientesMenuActivo && (
            <>
              <NavLink
                className="nav-link nav-sub-link"
                to="/clientes/pacientes"
                onClick={() => setSidebarOpen(false)}
              >
                <UsersRound size={17} />
                <span>Pacientes</span>
              </NavLink>
              <NavLink
                className="nav-link nav-sub-link"
                to="/clientes/profesionales"
                onClick={() => setSidebarOpen(false)}
              >
                <UsersRound size={17} />
                <span>Profesionales</span>
              </NavLink>
            </>
          )}
          <NavLink
            className="nav-link"
            to="/proveedores"
            onClick={() => setSidebarOpen(false)}
          >
            <Truck size={18} />
            <span>Proveedores</span>
          </NavLink>
          <NavLink
            className="nav-link"
            to="/productos"
            onClick={() => setSidebarOpen(false)}
          >
            <Package size={18} />
            <span>Productos</span>
          </NavLink>
          <NavLink
            className="nav-link"
            to="/cotizaciones"
            onClick={() => setSidebarOpen(false)}
          >
            <Calculator size={18} />
            <span>Cotizaciones</span>
          </NavLink>
          <NavLink
            className="nav-link nav-link-long"
            to="/mercaderia"
            onClick={() => setSidebarOpen(false)}
          >
            <Package size={18} />
            <span>Ingresos de envases/materias primas</span>
          </NavLink>
          <NavLink
            className="nav-link"
            to="/materiales-produccion"
            onClick={() => setSidebarOpen(false)}
          >
            <Package size={18} />
            <span>Materiales produccion</span>
          </NavLink>
          <NavLink
            className="nav-link"
            to="/notificaciones"
            onClick={() => setSidebarOpen(false)}
          >
            <Bell size={18} />
            <span>Notificaciones</span>
          </NavLink>
          <NavLink
            className="nav-link"
            to="/operaciones"
            onClick={() => setSidebarOpen(false)}
          >
            <ClipboardList size={18} />
            <span>Operaciones</span>
          </NavLink>
          {operacionesMenuActivo && (
            <>
              <NavLink
                className="nav-link nav-sub-link"
                to="/operaciones"
                end
                onClick={() => setSidebarOpen(false)}
              >
                <Package size={17} />
                <span>Produccion</span>
              </NavLink>
              <NavLink
                className="nav-link nav-sub-link"
                to="/operaciones/pedidos"
                onClick={() => setSidebarOpen(false)}
              >
                <ClipboardList size={17} />
                <span>Ordenes pedido</span>
              </NavLink>
              <NavLink
                className="nav-link nav-sub-link"
                to="/operaciones/fraccionamiento"
                onClick={() => setSidebarOpen(false)}
              >
                <ClipboardList size={17} />
                <span>Ordenes fraccionamiento</span>
              </NavLink>
              <NavLink
                className="nav-link nav-sub-link"
                to="/operaciones/produccion"
                onClick={() => setSidebarOpen(false)}
              >
                <ClipboardList size={17} />
                <span>Ordenes produccion</span>
              </NavLink>
            </>
          )}
          <NavLink
            className="nav-link"
            to="/usuarios"
            onClick={() => setSidebarOpen(false)}
          >
            <UserCog size={18} />
            <span>Usuarios</span>
          </NavLink>
        </nav>

        <div className="sidebar-footer">
          <div className="user-chip">
            <strong>{usuario?.nombre || "Administrador"}</strong>
            {usuario?.usuario || "admin"}
          </div>
          <button className="btn btn-ghost" onClick={logout}>
            <LogOut size={17} />
            Cerrar sesión
          </button>
        </div>
      </aside>

      <main className="main">
        <div className="mobile-app-header">
          <div className="mobile-brand">
            <img src={logo} alt="La Internacional" />
            <div>
              <strong>LaInternacional</strong>
              <span>Sistema administrativo</span>
            </div>
          </div>
          <div className="mobile-header-actions">
            <button
              type="button"
              className="mobile-icon-button"
              aria-label="Actualizar"
              onClick={() => window.location.reload()}
            >
              <RefreshCw size={18} />
            </button>
            <button
              type="button"
              className="mobile-icon-button"
              aria-label="Cerrar sesion"
              onClick={logout}
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
        <div className="topbar">
          <div className="topbar-heading">
            <button
              type="button"
              className="mobile-menu-button"
              aria-label="Abrir menu"
              aria-expanded={sidebarOpen}
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={20} />
            </button>
            <div>
              <h1>{title}</h1>
              <p>{subtitle}</p>
            </div>
          </div>
          <div className="topbar-actions">
            <NotificationBell />
            <button className="btn" onClick={() => window.location.reload()}>
              <RefreshCw size={17} />
              Actualizar
            </button>
          </div>
        </div>
        <div className="main-content">
          <Outlet />
        </div>
        <AppFooter />
      </main>
    </div>
  );
}
